import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { adminCustomerController, adminRideController, adminVehicleController } from '@/api/generated/endpoints';
import type { PaginationCursorPageResult, RideDTO } from '@/api/generated/types';
import { unwrap } from '@/api/http';
import { useCan } from '@/components/Can';
import { RideMinutes } from '@/components/RideMinutes';
import { usePanelNav } from '@/components/panel/panel';
import { UserScore } from '@/components/UserScore';
import { useCustomerLabels } from '@/features/customers/shared/useCustomerLabels';
import { useVehicleLabels } from '@/features/operations/vehicles/VehicleBits';
import { useRideLabels } from '@/features/operations/rides/useRideLabels';
import { formatDateTime, formatMoney, formatNumber, maskPhone } from '@/lib/format';
import { rideCharged } from '@/lib/rideCharge';
import { RideZoneTags } from '@/features/operations/rides/RideZoneTags';
import { useMarketCatalog } from '@/lib/markets';
import { useResult } from '@/lib/query';
import { vehicleLabel, vehicleTypeName } from '@/lib/vehicles';
import { useQuery } from '@tanstack/react-query';
import { Badge, TextLink } from '@/ui';
import { PeekCard, PeekFacts, PeekLink } from './Peek';

/** A customer's page; screens across features link to it. */
export const customerPath = (id: number | null | undefined) => `/customers/list/${id}`;
/** A ride's page. */
export const ridePath = (id: number | null | undefined) => `/operations/rides/${id}`;
/** A vehicle's page. */
export const vehiclePath = (id: number | null | undefined) => `/operations/vehicles/${id}`;
/** Opens a vehicle known only by its number (work orders, assets): resolved to its page on arrival. */
export const vehicleNumberPath = (n: number | string) => `/operations/vehicles/number/${n}`;

/**
 * Link to a customer ("—" without an id), previewing wallet, plans and the latest ride on hover.
 * `peek={false}` (inside a preview, or on the customer's own page) gives a plain link.
 */
export function CustomerLink({ id, children, peek = true }: { id: number | null | undefined; children?: ReactNode; peek?: boolean }) {
  const canRead = useCan('customer:read');
  if (id == null) return <>—</>;
  const label = children ?? `#${id}`;
  if (!peek || !canRead) return <TextLink to={customerPath(id)}>{label}</TextLink>;
  return (
    <PeekLink to={customerPath(id)} preview={() => <CustomerPeek id={id} />}>
      {label}
    </PeekLink>
  );
}

/**
 * Link to a ride, previewing its customer, vehicle, times and charge on hover. On a page with a
 * detail panel (the customer page) it opens the ride in the panel instead.
 */
export function RideLink({ id, children, peek = true }: { id: number | null | undefined; children?: ReactNode; peek?: boolean }) {
  const canRead = useCan('ride:read');
  const panel = usePanelNav();
  if (id == null) return <>—</>;
  const label = children ?? `#${id}`;
  if (panel && canRead) return <TextLink to={panel.href({ kind: 'ride', id })}>{label}</TextLink>;
  if (!peek || !canRead) return <TextLink to={ridePath(id)}>{label}</TextLink>;
  return (
    <PeekLink to={ridePath(id)} preview={() => <RidePeek id={id} />}>
      {label}
    </PeekLink>
  );
}

/** Link to a vehicle, previewing status, battery and connectivity on hover (or opening the panel, like RideLink). */
export function VehicleLink({ id, children, peek = true }: { id: number | null | undefined; children?: ReactNode; peek?: boolean }) {
  const canRead = useCan('vehicle:read');
  const panel = usePanelNav();
  if (id == null) return <>{children ?? '—'}</>;
  const label = children ?? `#${id}`;
  if (panel && canRead) return <TextLink to={panel.href({ kind: 'vehicle', id })}>{label}</TextLink>;
  if (!peek || !canRead) return <TextLink to={vehiclePath(id)}>{label}</TextLink>;
  return (
    <PeekLink to={vehiclePath(id)} preview={() => <VehiclePeek id={id} />}>
      {label}
    </PeekLink>
  );
}

/** A vehicle number linking to the vehicle (looked up only when clicked); plain text without vehicle:read. */
export function VehicleNumberLink({ number }: { number: number | string | null | undefined }) {
  const canRead = useCan('vehicle:read');
  if (number == null || number === '') return <>—</>;
  return canRead ? <TextLink to={vehicleNumberPath(number)}>{number}</TextLink> : <>{number}</>;
}

/** The customer's most recent ride (any market they rode in), shared by previews and the customer page. */
export function useLatestRide(customerId: number, enabled = true) {
  return useQuery({
    queryKey: ['customers', customerId, 'rides', 'latest'],
    queryFn: async () =>
      (unwrap(await adminRideController.search({ search: { customerId, pageSize: 1, cursor: null } })) as PaginationCursorPageResult<RideDTO> | null)?.content?.[0] ?? null,
    enabled,
    staleTime: 30_000,
  });
}

function CustomerPeek({ id }: { id: number }) {
  const { t } = useTranslation('common', { keyPrefix: 'peek' });
  const labels = useCustomerLabels();
  const canRides = useCan('ride:read');
  const customer = useResult(['customers', id], () => adminCustomerController.getCustomerById({ customerId: id }));
  const latest = useLatestRide(id, canRides);
  const c = customer.data;
  const status = labels.status(c?.status);
  const ride = latest.data;
  return (
    <PeekCard
      title={c?.fullName || t('customer', { id })}
      badges={
        c && (
          <>
            {status && <Badge tone={status.tone}>{status.label}</Badge>}
            {c.score != null && <UserScore score={c.score} showValue={false} />}
          </>
        )
      }
      to={customerPath(id)}
      loading={customer.isPending}
      error={customer.isError}
    >
      {c && (
        <PeekFacts
          items={[
            { label: t('phone'), value: c.phone ? `+${c.countryCode ?? 1} ${maskPhone(c.phone)}` : null },
            { label: t('rideCredit'), value: formatMoney(c.rideCredit) },
            { label: t('rides'), value: formatNumber(c.rideCount ?? 0) },
            { label: t('violations'), value: formatNumber(c.violationCount ?? 0) },
            {
              label: t('plans'),
              wide: true,
              value: (c.memberships?.length || c.ridePassMark) ? [...(c.memberships ?? []), ...(c.ridePassMark ? [t('ridePass')] : [])].join(' · ') : t('none'),
            },
            ...(canRides
              ? [
                  {
                    label: ride && !ride.end ? t('ridingNow') : t('latestRide'),
                    wide: true,
                    value: latest.isPending ? '…' : ride ? <RideSummary ride={ride} /> : t('none'),
                  },
                ]
              : []),
          ]}
        />
      )}
    </PeekCard>
  );
}

/** "#901 · 4821 E-Bike · Oct 8 15:02 · $3.50" with plain links (no nested previews). */
function RideSummary({ ride }: { ride: RideDTO }) {
  return (
    <span className="inline-flex flex-wrap gap-x-1.5">
      <TextLink to={ridePath(ride.id)}>#{ride.id}</TextLink>
      {ride.vehicleNumber != null && (
        <>
          <span aria-hidden>·</span>
          <TextLink to={vehiclePath(ride.vehicleId)}>
            {ride.vehicleNumber} {vehicleTypeName(ride.vehicleType)}
          </TextLink>
        </>
      )}
      <span aria-hidden>·</span>
      <span>{formatDateTime(ride.start)}</span>
      {ride.end && (
        <>
          <span aria-hidden>·</span>
          <span>{formatMoney(rideCharged(ride)?.total ?? ride.charge)}</span>
        </>
      )}
    </span>
  );
}

function RidePeek({ id }: { id: number }) {
  const { t } = useTranslation('common', { keyPrefix: 'peek' });
  const labels = useRideLabels();
  const markets = useMarketCatalog();
  const detail = useResult(['ride', id], () => adminRideController.getRideDetail({ rideId: id }));
  const r = detail.data?.ride;
  const state = labels.state(r?.state);
  return (
    <PeekCard
      title={t('ride', { id })}
      badges={r && (r.end ? state && <Badge tone={state.tone}>{state.label}</Badge> : <Badge tone="info" dot>{t('inProgress')}</Badge>)}
      to={ridePath(id)}
      loading={detail.isPending}
      error={detail.isError || (!detail.isPending && !r)}
    >
      {r && (
        <PeekFacts
          items={[
            { label: t('customerLabel'), value: r.customerId != null ? <TextLink to={customerPath(r.customerId)}>#{r.customerId}</TextLink> : null },
            {
              label: t('vehicle'),
              value: r.vehicleNumber != null ? <TextLink to={vehiclePath(r.vehicleId)}>{`${r.vehicleNumber} · ${vehicleTypeName(r.vehicleType)}`}</TextLink> : null,
            },
            { label: t('started'), value: formatDateTime(r.start) },
            { label: t('ended'), value: r.end ? formatDateTime(r.end) : t('inProgress') },
            { label: t('minutes'), value: <RideMinutes ride={r} /> },
            { label: t('charged'), value: formatMoney(rideCharged(r)?.total ?? r.charge) },
            { label: t('zones'), value: <RideZoneTags ride={r} empty="—" /> },
            { label: t('market'), value: markets.name(r.areaId) },
          ]}
        />
      )}
    </PeekCard>
  );
}

function VehiclePeek({ id }: { id: number }) {
  const { t } = useTranslation('common', { keyPrefix: 'peek' });
  const labels = useVehicleLabels();
  const markets = useMarketCatalog();
  const vehicle = useResult(['vehicle', id], () => adminVehicleController.getById({ vehicleId: id }));
  const v = vehicle.data;
  const status = labels.status(v?.status);
  const pct = (n: number | null | undefined) => (n == null ? null : `${n}%`);
  return (
    <PeekCard
      title={v ? `${vehicleLabel(v)} · ${vehicleTypeName(v.vehicleType)}` : t('vehicleTitle', { id })}
      badges={
        v && (
          <>
            {status && <Badge tone={status.tone}>{status.label}</Badge>}
            <Badge tone={v.connected ? 'success' : 'neutral'} dot>
              {v.connected ? t('online') : t('offline')}
            </Badge>
          </>
        )
      }
      to={vehiclePath(id)}
      loading={vehicle.isPending}
      error={vehicle.isError}
    >
      {v && (
        <PeekFacts
          items={[
            { label: t('battery'), value: pct(v.vehicleBattery) },
            { label: t('iotBattery'), value: pct(v.iotBattery) },
            { label: t('lock'), value: v.inUse ? t('inUse') : v.locked ? t('locked') : t('unlocked') },
            { label: t('market'), value: markets.name(v.areaId) },
            { label: t('lastRide'), value: formatDateTime(v.lastRideTime) },
            { label: t('heartbeat'), value: formatDateTime(v.heartTime) },
            { label: t('note'), wide: true, value: v.statusNote },
          ]}
        />
      )}
    </PeekCard>
  );
}
